import { marked } from 'marked';
import DOMPurify from 'dompurify';

export function createUUID(): string {
  const webCrypto = globalThis.crypto;
  const bytes = webCrypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join('-');
}


// function splitBracketedText(str: string) {
//   // Match the full pattern: before [brackets] (link) after
//   const regex = /(.*?)\[(.*?)\]\s*\([^)]*\)(.*)/;
//   const match = str.match(regex);

//   if (!match) return [str, "", ""]; // fallback if pattern not found

//   return [
//     match[1].trim(), // text before brackets
//     match[2].trim(), // text inside brackets
//     match[3].trim()  // text after, link removed
//   ];
// }


// export const convertMarkdownToHTML = (text: string): string => {
//   try {
//     const renderer = new marked.Renderer();
//     renderer.link = ({ href, title, text }) => {
//       debugger
//       return `<a href="${href}" target="_blank" rel="noopener noreferrer" ${title ? `title="${title}"` : ''}>${text}</a>`;
//     };

//     marked.setOptions({
//       renderer: renderer,
//       gfm: true,
//       breaks: true
//     });
//     // Convert markdown to HTML with custom renderer
//     const rawHtml = marked(text);
//     // Sanitize HTML to prevent XSS attacks
//     const cleanHtml = DOMPurify.sanitize(rawHtml as string);
//     return cleanHtml;
//   } catch (error) {
//     console.error('Error converting markdown to HTML:', error);
//     return text; // Return original text if conversion fails
//   }
// };

function safeSplitFirstMarkdownLink(text: string) {
  const startBracket = text.indexOf('[');
  if (startBracket === -1) return null;
  const endBracket = text.indexOf(']', startBracket + 1);
  if (endBracket === -1) return null;

  let i = endBracket + 1;
  while (i < text.length && /\s/.test(text[i])) i++;
  if (i >= text.length || text[i] !== '(') return null;

  const startParen = i;
  const endParen = text.indexOf(')', startParen + 1);
  if (endParen === -1) return null;

  const before = text.slice(0, startBracket);
  const label = text.slice(startBracket + 1, endBracket);
  const parenContent = text.slice(startParen + 1, endParen);
  const after = text.slice(endParen + 1);
  return { before, label, parenContent, after };
}

// Tags forbidden by the chat widget's markdown sanitizer. Images are not a
// supported message medium in chat, so any <img>/<picture>/<source> emitted
// by marked or pasted as raw HTML must be stripped before render.
const FORBIDDEN_HTML_TAGS = ['img', 'picture', 'source'];

// Strip markdown image syntax (`![alt](url)` and reference-style
// `![alt][ref]` plus its `[ref]: url` definition) before handing the text to
// marked. Doing this pre-parse means alt text and surrounding whitespace are
// dropped cleanly instead of leaving stray brackets in the rendered output.
const stripMarkdownImageSyntax = (text: string): string => {
  const withoutInlineImages = text.replace(/!\[[^\]]*\]\([^)]*\)/g, '');
  const withoutReferenceImages = withoutInlineImages.replace(/!\[[^\]]*\]\[[^\]]*\]/g, '');
  return withoutReferenceImages.replace(/^\s*\[[^\]]+\]:\s*\S+(?:\s+["'][^"']*["'])?\s*$/gm, '');
};

export const convertMarkdownToHTML = (text: string): string => {
  try {
    const renderer = new marked.Renderer();
    renderer.link = ({ href, title, tokens }: any) => {
      const innerText = tokens ? tokens.map((t: any) => t.raw).join('') : '';
      return `<a href="${href}" target="_blank" rel="noopener noreferrer"${title ? ` title="${title}"` : ''}> ${innerText}</a>`;
    };
    // Defense-in-depth: even if image markdown slips past the pre-parse strip
    // (e.g. via an extension), force the renderer to emit nothing for images.
    renderer.image = () => '';
    marked.setOptions({ renderer, gfm: true, breaks: true });

    const sanitizedInput = stripMarkdownImageSyntax(text);
    const split = safeSplitFirstMarkdownLink(sanitizedInput);

    const inputForMarked = split
      ? (() => {
          const { before, label, parenContent, after } = split;
          const hrefMatch = /^\s*<?([^>\s]+)>?(?:\s+["'](.+?)["'])?\s*$/.exec(parenContent);
          const href = hrefMatch ? hrefMatch[1] : parenContent.trim();
          const title = hrefMatch && hrefMatch[2] ? hrefMatch[2] : '';
          const titleAttr = title ? ` title="${title.replace(/"/g, '&quot;')}"` : '';
          return `${before}<a href="${href}" target="_blank" rel="noopener noreferrer"${titleAttr}> ${label}</a>${after}`;
        })()
      : sanitizedInput;

    const rawHtml = marked.parse(inputForMarked);
    const cleanHtml = DOMPurify.sanitize(rawHtml as string, {
      FORBID_TAGS: FORBIDDEN_HTML_TAGS,
      FORBID_ATTR: ['srcset']
    });
    return cleanHtml;
  } catch (error) {
    console.error('Error converting markdown to HTML:', error);
    return text;
  }
};